import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import PerButtonCircle from '../../../components/custom_components/ButtonCircle'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import PerSpacer from '../../../components/custom_components/Spacer'
import { TargetsModal } from '../../../components/custom_components/TargetsModal/TargetsModal'
import { useTargetsModalStore } from '../../../components/custom_components/TargetsModal/TargetsModalStore'
import { TargetModalType } from '../../../components/custom_components/TargetsModal/types'
import PerText from '../../../components/custom_components/Text'
import { useAuth } from '../../../context/auth-context'
import useObjectiveWithModalInMeeting from '../../../graphql/objectives/useObjectiveWithModalInMeeting'
import { useDateTime } from '../../../shared/useDateTime'
import {
  FullMeetingFragment,
  ObjectiveStatus,
  ObjectiveDto,
  SalaryOfYearFragment,
  TargetBonusOfYearFragment,
  ContractChangeFragment,
  KeyResultInput,
  AppraisalSeasonDto,
  Currency,
} from '../../../types'
import { calculatePastTargetsData } from '../appraisal-flow/shared/util'
import { ContractWithValidity, FlexibleBonusOverride } from '../appraisal-flow/shared/targetBonusCalculation-util'
import { ObjectiveList, ObjectiveListType } from './lists/ObjectivesList'
import { filterObjectivesByTypeStatusAndRefYear } from './util'
import { retrieveCurrencyConversionRate } from '../../../shared/currencyConversionUtil'
import { isDefined } from '../../../types.helper'
import { useCurrency } from '../../../context/currency-context'

interface Props {
  meeting: FullMeetingFragment
  objectives: ObjectiveDto[]
  contractStartDate: string
  isPreparationStep?: boolean
  targetBonusOfYear?: TargetBonusOfYearFragment[]
  salaryOfYear: SalaryOfYearFragment[]
  currentSalary: number
  currentBonus: number
  contractEndDate?: string
  employeeTimezone: string
  isBonusValidationEnabled: boolean
  contractChangesByEmployee?: ContractChangeFragment[]
  contractsInYear?: ContractWithValidity[]
  pastYearFlexibleBonusOverride?: FlexibleBonusOverride
  flexibleBonusUnknownCurrency?: Currency
}

export const ManagerExistingObjectives: FC<Props> = ({
  meeting,
  objectives,
  targetBonusOfYear,
  contractStartDate,
  contractEndDate,
  salaryOfYear,
  currentSalary,
  currentBonus,
  employeeTimezone,
  isBonusValidationEnabled,
  contractChangesByEmployee = [],
  contractsInYear = [],
  pastYearFlexibleBonusOverride,
  flexibleBonusUnknownCurrency,
}) => {
  const [t] = useTranslation()
  const { user } = useAuth()
  const { compareDates, formatDateToUtcString, utcDateStringToMoment, calcStartOfTheYearMoment, calcEndOfTheYearMoment } = useDateTime(user)

  const openTargetModal = useTargetsModalStore((state) => state.openTargetModal)
  const { currency: currentContractCurrency, currencyConversionData } = useCurrency()

  const {
    id: meetingId,
    employee,
    targetsCalculationHelper: { pastTargetsReferenceYear },
  } = meeting

  const replyEntryDateMoment = utcDateStringToMoment(employee.replyEntryDate)

  let contractTargetBonus = 0
  if (targetBonusOfYear) {
    const { targetBonusAmount } = calculatePastTargetsData(
      employeeTimezone,
      isBonusValidationEnabled,
      targetBonusOfYear,
      salaryOfYear,
      currentSalary,
      currentBonus,
      contractChangesByEmployee,
      objectives,
      currencyConversionData,
      currentContractCurrency,
      pastTargetsReferenceYear,
      contractStartDate,
      contractEndDate,
      contractsInYear,
      pastYearFlexibleBonusOverride,
      flexibleBonusUnknownCurrency,
    )
    contractTargetBonus = targetBonusAmount
  }

  const { handleObjectiveDismiss, handleObjectiveReactivate } = useObjectiveWithModalInMeeting({
    meetingId,
  })

  // Changes in target bonus have an influence on the UI, we only take into consideration
  // targets that have status Approved or PastApproved
  const currentlyPlannedTargetBonus = objectives
    .filter((o) => [ObjectiveStatus.Approved, ObjectiveStatus.PastApproved].includes(o.status) && o.targetAmount)
    .reduce((sum, obj) => {
      if (!obj.targetAmount) {
        return sum
      }
      if (obj.currency! === currentContractCurrency) {
        return sum + obj.targetAmount
      }
      const conversionRate: number | undefined = retrieveCurrencyConversionRate(
        obj.currency!,
        currentContractCurrency,
        currencyConversionData,
      )!
      if (conversionRate === undefined) {
        return sum + obj.targetAmount
      }
      return sum + Number((obj.targetAmount * conversionRate).toFixed(2))
    }, 0)
  const bonusDifference = contractTargetBonus - currentlyPlannedTargetBonus
  const changeInTargetBonus = bonusDifference > 0

  let targetsStartDate = calcStartOfTheYearMoment(meeting.appraisalSeason.year - 1)

  if (changeInTargetBonus) {
    const diffDates = compareDates(employee.replyEntryDate, formatDateToUtcString(targetsStartDate))
    if (replyEntryDateMoment !== undefined && diffDates > 0) {
      targetsStartDate = replyEntryDateMoment.toDate()
    }
  }

  const monetaryObjectives = filterObjectivesByTypeStatusAndRefYear(
    objectives,
    true,
    [ObjectiveStatus.Approved, ObjectiveStatus.PastApproved, ObjectiveStatus.DismissedPending],
    pastTargetsReferenceYear,
  )

  const developmentObjectives = filterObjectivesByTypeStatusAndRefYear(
    objectives,
    false,
    [ObjectiveStatus.Approved, ObjectiveStatus.MarkedAsDone, ObjectiveStatus.PastApproved, ObjectiveStatus.DismissedPending],
    pastTargetsReferenceYear,
  )

  const handleObjectiveClick = (objective?: ObjectiveDto | undefined) => {
    if (!objective) {
      const templateObjective: ObjectiveDto = {
        id: undefined,
        title: '',
        targetAmount: null,
        currency: currentContractCurrency,
        status: ObjectiveStatus.PastApproved,
        start: targetsStartDate.toISOString(),
        due: calcEndOfTheYearMoment(meeting.appraisalSeason.year - 1).toISOString(),
        keyResults: [{}] as KeyResultInput[],
        noteList: [],
        appraisalSeason: {} as AppraisalSeasonDto,
      }
      openTargetModal(TargetModalType.appraisalNewPastTarget, templateObjective)
    } else {
      const conversioneRate: number | undefined = !!objective.currency
        ? retrieveCurrencyConversionRate(objective.currency, currentContractCurrency, currencyConversionData)
        : undefined
      if (objective.status === ObjectiveStatus.Approved || objective.status === ObjectiveStatus.MarkedAsDone) {
        openTargetModal(TargetModalType.appraisalEditPastTarget, objective, conversioneRate)
      } else if (objective.status === ObjectiveStatus.PastApproved) {
        openTargetModal(TargetModalType.appraisalNewPastTarget, objective, conversioneRate)
      } else if (objective.status === ObjectiveStatus.Dismissed || objective.status === ObjectiveStatus.DismissedPending) {
        openTargetModal(TargetModalType.viewTargetInMeeting, objective, conversioneRate)
      }
    }
  }

  const onObjectiveDeletion = (objective?: ObjectiveDto): void => {
    if (!!objective) {
      openTargetModal(
        TargetModalType.deleteTarget,
        objective,
        isDefined(objective.targetAmount)
          ? retrieveCurrencyConversionRate(objective.currency!, currentContractCurrency, currencyConversionData)
          : undefined,
      )
    }
  }

  const handleObjectiveDuplicateAndDismissOrDelete = (objective?: ObjectiveDto) => {
    if (!!objective) {
      openTargetModal(
        TargetModalType.duplicateAndDismissOrDeletePastTarget,
        objective,
        isDefined(objective.targetAmount)
          ? retrieveCurrencyConversionRate(objective.currency!, currentContractCurrency, currencyConversionData)
          : undefined,
      )
    }
  }

  return (
    <>
      <PerFlexRow justify="space-between" align="middle" mb="xs">
        <PerText appearance="heading" color="primary">
          {t('okr.instructions.manager.review', { name: employee.chosenFirstName ? employee.chosenFirstName : employee.legalFirstName })}
        </PerText>

        <PerButtonCircle
          icon="add_stroke"
          color="success"
          variant="pastel"
          tooltip={t('appraisalMeeting.buttons.newGoals')}
          onClick={() => handleObjectiveClick()}
          data-e2e-add-more-targets-button
        />
      </PerFlexRow>

      {((contractTargetBonus > 0 && meeting.allowedMonetaryTargets.pastTargetsAllowed) || !!monetaryObjectives.length) && (
        <PerSpacer mb="m">
          <ObjectiveList
            listType={ObjectiveListType.PastMonetary}
            objectives={monetaryObjectives}
            employeeFirstName={employee.chosenFirstName ? employee.chosenFirstName : employee.legalFirstName}
            handleObjectiveClicked={(_, objective) => handleObjectiveClick(objective)}
            handleObjectiveDismiss={handleObjectiveDismiss}
            handleObjectiveDelete={(objective?: ObjectiveDto) => onObjectiveDeletion(objective)}
            handleObjectiveReactivate={handleObjectiveReactivate}
            moneyEditable={true}
            toBePaid
            showStatus
            showToBePaidAndBonus
            handleObjectiveDuplicateAndDismissOrDelete={handleObjectiveDuplicateAndDismissOrDelete}
          />
        </PerSpacer>
      )}

      <ObjectiveList
        objectives={developmentObjectives}
        employeeFirstName={employee.chosenFirstName ? employee.chosenFirstName : employee.legalFirstName}
        handleObjectiveClicked={(_, objective) => handleObjectiveClick(objective)}
        handleObjectiveDismiss={handleObjectiveDismiss}
        handleObjectiveDelete={(objective?: ObjectiveDto) => onObjectiveDeletion(objective)}
        handleObjectiveReactivate={handleObjectiveReactivate}
        listType={ObjectiveListType.PastDevelopment}
        showStatus
        showToBePaidAndBonus
      />
      <TargetsModal />
    </>
  )
}
